import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Eye, Plus, Truck, Search, Layers, ChevronLeft, ChevronRight, DollarSign, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';
import EstadoBadge from './EstadoBadge';
import CambiarEstadoModal from './CambiarEstadoModal';
import PaqueteFormModal from './PaqueteFormModal';
import PaqueteDetalleModal from './PaqueteDetalleModal';
import UltimaMillaModal from './UltimaMillaModal';
import UsuariosPanel from './UsuariosPanel';
import ConsolidarModal from './ConsolidarModal';
import ConsolidadoDetalleModal from './ConsolidadoDetalleModal';
import LiquidarModal from './LiquidarModal';
import LiquidacionImagenModal from './LiquidacionImagenModal';
import FacturasPanel from './FacturasPanel';
import DashboardAdmin from './DashboardAdmin';
import LeadsPanel from './LeadsPanel';
import RentabilidadPanel from './RentabilidadPanel';
import OperadorLogisticoPanel from './OperadorLogisticoPanel';
import PrealertasOperadorPanel from './PrealertasOperadorPanel';
import CompraAsistidaPanel from './CompraAsistidaPanel';
import PuertaPuertaPanel from './PuertaPuertaPanel';
import ErroresClientesPanel from './ErroresClientesPanel';
import AdminSidebar from './AdminSidebar';

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: '2-digit' });
}

const PAIS_LABEL = { usa: 'Miami', china: 'Shenzhen' };

function agruparPorConsolidacion(paquetes) {
  const sueltos = [];
  const grupos = {};
  for (const p of paquetes) {
    if (!p.consolidacion_detalle) {
      sueltos.push({ tipo: 'paquete', data: p });
    } else {
      const cid = p.consolidacion_detalle.id;
      if (!grupos[cid]) {
        grupos[cid] = {
          tipo: 'consolidado',
          consolidacion_id: cid,
          guia_agrupada: p.consolidacion_detalle.guia_agrupada,
          cliente_detalle: p.cliente_detalle,
          paquetes: [],
        };
      }
      grupos[cid].paquetes.push(p);
    }
  }
  const filas = [...Object.values(grupos), ...sueltos];
  return filas;
}

// Valor por el que se ordena cada columna. Un consolidado se ordena como una
// sola fila (con los datos de su primer paquete), igual que se muestra.
function valorOrden(fila, campo) {
  const esGrupo = fila.tipo === 'consolidado';
  const p = esGrupo ? fila.paquetes[0] : fila.data;
  const cliente = esGrupo ? fila.cliente_detalle : p?.cliente_detalle;
  switch (campo) {
    case 'casillero': return cliente?.codigo_casillero || '';
    case 'cliente': return cliente?.nombre || '';
    case 'guia': return (esGrupo ? fila.guia_agrupada : p?.tracking) || '';
    case 'origen': return p?.pais_origen || '';
    case 'fecha': return p?.fecha_prealerta || '';
    case 'estado': return p?.estado_actual_detalle?.orden ?? '';
    default: return '';
  }
}

function comparar(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });
}

// Los vacíos siempre quedan al final, sea cual sea la dirección. A igual
// valor, lo más reciente primero.
function ordenarFilas(filas, { campo, dir }) {
  const signo = dir === 'asc' ? 1 : -1;
  return [...filas].sort((a, b) => {
    const va = valorOrden(a, campo);
    const vb = valorOrden(b, campo);
    if (va === '' && vb !== '') return 1;
    if (vb === '' && va !== '') return -1;
    const c = comparar(va, vb);
    if (c !== 0) return c * signo;
    return comparar(valorOrden(b, 'fecha'), valorOrden(a, 'fecha'));
  });
}

export default function StaffPanel({ rol }) {
  const [paquetes, setPaquetes] = useState([]);
  const [estados, setEstados] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null);
  const [editandoPaquete, setEditandoPaquete] = useState(null);
  const [creandoPaquete, setCreandoPaquete] = useState(false);
  const [ultimaMillaPaquete, setUltimaMillaPaquete] = useState(null);
  const [tab, setTab] = useState('paquetes');
  const [orden, setOrden] = useState({ campo: 'fecha', dir: 'desc' });
  const [seleccionados, setSeleccionados] = useState({});
  const [mostrarConsolidar, setMostrarConsolidar] = useState(false);
  const [detalleConsolidado, setDetalleConsolidado] = useState(null);
  const [ultimaMillaGuiaOverride, setUltimaMillaGuiaOverride] = useState(null);
  const [editandoEstadoGrupo, setEditandoEstadoGrupo] = useState(null);
  const [detallePaquete, setDetallePaquete] = useState(null);
  const [subTab, setSubTab] = useState('en_curso');
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);
  const [liquidandoPaquetes, setLiquidandoPaquetes] = useState(null);
  const [imagenLiquidacionId, setImagenLiquidacionId] = useState(null);
  const [solicitudesNuevas, setSolicitudesNuevas] = useState(0);
  const [puertaPuertaNuevas, setPuertaPuertaNuevas] = useState(0);
  const [erroresCriticos, setErroresCriticos] = useState(0);
  const [prealertasPendientes, setPrealertasPendientes] = useState(0);

  const refrescarErrores = () => {
    api.get('/errores-usuarios/resumen/')
      .then((res) => setErroresCriticos(res.data.criticos_pendientes))
      .catch(() => {});
  };

  const refrescarPrealertasPendientes = () => {
    api.get('/paquetes/pendientes-operador/')
      .then((res) => setPrealertasPendientes((res.data.paquetes || []).length))
      .catch(() => {});
  };

  useEffect(() => {
    if (rol !== 'admin') return;
    refrescarPrealertasPendientes();
    api.get('/compra-asistida/', { params: { estado: 'nueva' } })
      .then((res) => setSolicitudesNuevas((res.data.results || res.data).length))
      .catch(() => {});
    api.get('/puerta-a-puerta/', { params: { estado: 'nueva' } })
      .then((res) => setPuertaPuertaNuevas((res.data.results || res.data).length))
      .catch(() => {});
    refrescarErrores();
  }, [rol, tab]);

  const cargar = () => {
    setCargando(true);
    setSeleccionados({});
    const params = { ordering: '-creado' };
    if (filtroEstado) params.estado_actual = filtroEstado;
    if (filtroCliente) params.search = filtroCliente;
    api.get('/paquetes/', { params })
      .then((res) => setPaquetes(res.data.results || res.data))
      .catch(() => toast.error('No se pudieron cargar los paquetes.'))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    api.get('/estados/').then((res) => setEstados(res.data.results || res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setFiltroCliente(busquedaCliente.trim()), 350);
    return () => clearTimeout(timer);
  }, [busquedaCliente]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [filtroEstado, filtroCliente]);

  useEffect(() => setPagina(1), [subTab, porPagina, filtroEstado, filtroCliente, orden]);

  const filas = useMemo(() => ordenarFilas(agruparPorConsolidacion(paquetes), orden), [paquetes, orden]);

  const cambiarOrden = (campo) => setOrden((o) => (
    o.campo === campo
      ? { campo, dir: o.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: campo === 'fecha' ? 'desc' : 'asc' }
  ));

  const cabecera = (campo, etiqueta) => {
    const activo = orden.campo === campo;
    const Icono = !activo ? ArrowUpDown : orden.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th aria-sort={activo ? (orden.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        <button type="button" className="th-orden" onClick={() => cambiarOrden(campo)} title={`Ordenar por ${etiqueta.toLowerCase()}`}>
          {etiqueta} <Icono size={12} style={{ opacity: activo ? 1 : 0.35 }} />
        </button>
      </th>
    );
  };

  const ESTADOS_OTROS = new Set(['cancelado_devuelto', 'retenido_ecuafly', 'entregado_ecuafly']);

  const codigoEstado = (fila) => {
    const estado = fila.tipo === 'consolidado'
      ? fila.paquetes[0]?.estado_actual_detalle
      : fila.data?.estado_actual_detalle;
    return estado?.codigo || '';
  };

  const filasEnCurso = useMemo(() => filas.filter((f) => {
    const c = codigoEstado(f);
    return c !== 'entregado' && !ESTADOS_OTROS.has(c);
  }), [filas]);
  const filasEntregados = useMemo(() => filas.filter((f) => codigoEstado(f) === 'entregado'), [filas]);
  const filasOtros = useMemo(() => filas.filter((f) => ESTADOS_OTROS.has(codigoEstado(f))), [filas]);
  const filasActivas = subTab === 'en_curso' ? filasEnCurso : subTab === 'entregados' ? filasEntregados : filasOtros;

  const totalPaginas = Math.max(1, Math.ceil(filasActivas.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const filasPaginadas = filasActivas.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);

  const paquetesSueltos = useMemo(
    () => paquetes.filter((p) => !p.consolidacion_detalle),
    [paquetes]
  );

  const toggleSeleccion = (id) => {
    setSeleccionados((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else {
        const p = paquetesSueltos.find((p) => p.id === id);
        if (p) next[id] = p;
      }
      return next;
    });
  };

  const seleccionadosArr = Object.values(seleccionados);
  const puedeConsolidar = useMemo(() => {
    if (seleccionadosArr.length < 2) return false;
    const clientes = new Set(seleccionadosArr.map((p) => p.cliente));
    return clientes.size === 1;
  }, [seleccionadosArr]);

  const desconsolidar = async (consolidacion_id) => {
    if (!window.confirm('¿Desconsolidar este grupo? Los paquetes volverán a aparecer individualmente.')) return;
    try {
      await api.post('/paquetes/desconsolidar/', { consolidacion_id });
      toast.success('Consolidación deshecha.');
      cargar();
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudo desconsolidar.'));
    }
  };

  return (
    <div className={rol === 'admin' ? 'admin-shell' : undefined}>
      {rol === 'admin' && (
        <AdminSidebar
          tab={tab}
          onChange={setTab}
          badges={{
            compra_asistida: solicitudesNuevas,
            puerta_puerta: puertaPuertaNuevas,
            errores: erroresCriticos,
            prealertas_operador: prealertasPendientes,
          }}
        />
      )}

      <div className={rol === 'admin' ? 'admin-content' : undefined}>
      {tab === 'dashboard' ? <DashboardAdmin /> : tab === 'usuarios' ? <UsuariosPanel /> : tab === 'facturas' ? <FacturasPanel /> : tab === 'leads' ? <LeadsPanel /> : tab === 'rentabilidad' ? <RentabilidadPanel /> : tab === 'operador' ? <OperadorLogisticoPanel /> : tab === 'prealertas_operador' ? <PrealertasOperadorPanel onCambio={refrescarPrealertasPendientes} /> : tab === 'compra_asistida' ? <CompraAsistidaPanel /> : tab === 'puerta_puerta' ? <PuertaPuertaPanel /> : tab === 'errores' ? <ErroresClientesPanel onCambio={refrescarErrores} /> : (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)', gap: 'var(--spacing-3)' }}>
        <h2 style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Paquetes</h2>
        <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
            <input
              type="text" placeholder="Buscar cliente, guía, tracking..." value={busquedaCliente}
              onChange={(e) => setBusquedaCliente(e.target.value)}
              style={{ minWidth: '180px', paddingLeft: '32px' }}
            />
          </div>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} style={{ minWidth: '180px' }}>
            <option value="">Todos los estados</option>
            {estados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
          {seleccionadosArr.length >= 2 && (
            <button
              className="btn-primary"
              disabled={!puedeConsolidar}
              onClick={() => setMostrarConsolidar(true)}
              title={!puedeConsolidar ? 'Todos los paquetes deben ser del mismo cliente' : ''}
              style={{ whiteSpace: 'nowrap' }}
            >
              <Layers size={14} /> Consolidar ({seleccionadosArr.length})
            </button>
          )}
          <button className="btn-secondary" onClick={cargar} style={{ whiteSpace: 'nowrap' }}><RefreshCw size={14} /> Actualizar</button>
          <button className="btn-primary" onClick={() => setCreandoPaquete(true)} style={{ whiteSpace: 'nowrap' }}><Plus size={14} /> Nuevo paquete</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
        <button className={`tab-btn ${subTab === 'en_curso' ? 'active' : ''}`} onClick={() => setSubTab('en_curso')}>
          En curso ({filasEnCurso.length})
        </button>
        <button className={`tab-btn ${subTab === 'entregados' ? 'active' : ''}`} onClick={() => setSubTab('entregados')}>
          Entregados ({filasEntregados.length})
        </button>
        <button className={`tab-btn ${subTab === 'otros' ? 'active' : ''}`} onClick={() => setSubTab('otros')}>
          Otros estados ({filasOtros.length})
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{ width: '36px' }}></th>
              {cabecera('casillero', 'Casillero')}
              {cabecera('cliente', 'Cliente')}
              {cabecera('guia', 'Tracking / Guía')}
              {cabecera('origen', 'Origen')}
              {cabecera('fecha', 'Fecha')}
              {cabecera('estado', 'Estado')}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={8} className="text-secondary">Cargando...</td></tr>
            ) : filasActivas.length === 0 ? (
              <tr><td colSpan={8} className="text-secondary">{subTab === 'en_curso' ? 'No hay paquetes en curso.' : subTab === 'entregados' ? 'No hay paquetes entregados.' : 'No hay paquetes en otros estados.'}</td></tr>
            ) : filasPaginadas.map((fila) => {
              if (fila.tipo === 'consolidado') {
                const grupo = fila;
                return (
                  <tr key={`cons-${grupo.consolidacion_id}`} style={{ background: 'var(--color-bg-secondary)' }}>
                    <td></td>
                    <td className="mono">{grupo.cliente_detalle?.codigo_casillero}</td>
                    <td>{grupo.cliente_detalle?.nombre}</td>
                    <td>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                        onClick={() => setDetalleConsolidado({
                          consolidacion_id: grupo.consolidacion_id,
                          guia_agrupada: grupo.guia_agrupada,
                          paquetes: grupo.paquetes,
                        })}
                        title="Ver contenido del consolidado"
                      >
                        <Layers size={14} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                        <div>
                          <strong style={{ color: 'var(--color-primary)' }}>{grupo.guia_agrupada}</strong>
                          <div className="text-xs text-secondary">Consolidado ({grupo.paquetes.length} paquetes)</div>
                        </div>
                      </div>
                    </td>
                    <td>{PAIS_LABEL[grupo.paquetes[0]?.pais_origen] || grupo.paquetes[0]?.pais_origen}</td>
                    <td className="text-sm text-secondary">{formatFecha(grupo.paquetes[0]?.fecha_prealerta)}</td>
                    <td>
                      <EstadoBadge estado={grupo.paquetes[0]?.estado_actual_detalle} />
                    </td>
                    <td className="text-right">
                      <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
                        {grupo.guia_agrupada && (
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => {
                              setUltimaMillaGuiaOverride(grupo.guia_agrupada);
                              setUltimaMillaPaquete(grupo.paquetes[0]);
                            }}
                            title="Generar texto de última milla"
                          >
                            <Truck size={14} />
                          </button>
                        )}
                        <button className="btn-ghost btn-sm" onClick={() => setDetallePaquete(grupo.paquetes[0])}>
                          <Eye size={14} /> Ver
                        </button>
                        <button className="btn-ghost btn-sm" onClick={() => setLiquidandoPaquetes(grupo.paquetes)} title={grupo.paquetes[0].liquidacion_activa ? 'Ver liquidación' : 'Generar liquidación'}>
                          <DollarSign size={14} />
                        </button>
                        <button className="btn-ghost btn-sm" onClick={() => setEditandoEstadoGrupo(grupo.paquetes)}>
                          Cambiar estado
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }

              const p = fila.data;
              return (
                <tr key={p.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!seleccionados[p.id]}
                      onChange={() => toggleSeleccion(p.id)}
                    />
                  </td>
                  <td className="mono">{p.cliente_detalle?.codigo_casillero}</td>
                  <td>{p.cliente_detalle?.nombre}</td>
                  <td>
                    <span style={{ cursor: 'pointer', color: 'var(--color-primary)' }} onClick={() => setDetallePaquete(p)} title="Ver detalle y seguimiento">
                      {p.tracking}
                    </span>
                    {p.guia && <div className="text-xs text-secondary">{p.guia}</div>}
                  </td>
                  <td>{PAIS_LABEL[p.pais_origen] || p.pais_origen}</td>
                  <td className="text-sm text-secondary">{formatFecha(p.fecha_prealerta)}</td>
                  <td><EstadoBadge estado={p.estado_actual_detalle} /></td>
                  <td className="text-right">
                    <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
                      {p.guia && (
                        <button className="btn-ghost btn-sm" onClick={() => setUltimaMillaPaquete(p)} title="Generar texto de última milla">
                          <Truck size={14} />
                        </button>
                      )}
                      <button className="btn-ghost btn-sm" onClick={() => setDetallePaquete(p)}>
                        <Eye size={14} /> Ver
                      </button>
                      <button className="btn-ghost btn-sm" onClick={() => setLiquidandoPaquetes([p])} title={p.liquidacion_activa ? 'Ver liquidación' : 'Generar liquidación'}>
                        <DollarSign size={14} />
                      </button>
                      <button className="btn-ghost btn-sm" onClick={() => setEditando(p)}>
                        Cambiar estado
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filasActivas.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-4)', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">Mostrar</span>
            <select value={porPagina} onChange={(e) => setPorPagina(Number(e.target.value))} style={{ width: 'auto', minWidth: '70px' }}>
              {[10, 20, 30, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-sm text-secondary">por página</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">
              {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, filasActivas.length)} de {filasActivas.length}
            </span>
            <button className="btn-ghost btn-sm" disabled={paginaActual <= 1} onClick={() => setPagina(paginaActual - 1)}>
              <ChevronLeft size={16} />
            </button>
            <button className="btn-ghost btn-sm" disabled={paginaActual >= totalPaginas} onClick={() => setPagina(paginaActual + 1)}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {editando && (
        <CambiarEstadoModal
          paquete={editando}
          onClose={() => setEditando(null)}
          onCambiado={() => { setEditando(null); cargar(); }}
        />
      )}
      {creandoPaquete && (
        <PaqueteFormModal
          onClose={() => setCreandoPaquete(false)}
          onGuardado={() => { setCreandoPaquete(false); cargar(); }}
        />
      )}
      {editandoPaquete && (
        <PaqueteFormModal
          paquete={editandoPaquete}
          onClose={() => setEditandoPaquete(null)}
          onGuardado={() => { setEditandoPaquete(null); cargar(); }}
        />
      )}
      {ultimaMillaPaquete && (
        <UltimaMillaModal
          paquete={ultimaMillaPaquete}
          guiaOverride={ultimaMillaGuiaOverride}
          onClose={() => { setUltimaMillaPaquete(null); setUltimaMillaGuiaOverride(null); }}
        />
      )}
      {mostrarConsolidar && (
        <ConsolidarModal
          paquetes={seleccionadosArr}
          onClose={() => setMostrarConsolidar(false)}
          onConsolidado={() => { setMostrarConsolidar(false); cargar(); }}
        />
      )}
      {editandoEstadoGrupo && (
        <CambiarEstadoModal
          paquete={editandoEstadoGrupo[0]}
          paquetes={editandoEstadoGrupo}
          onClose={() => setEditandoEstadoGrupo(null)}
          onCambiado={() => { setEditandoEstadoGrupo(null); cargar(); }}
        />
      )}
      {detallePaquete && (
        <PaqueteDetalleModal
          paquete={detallePaquete}
          onClose={() => setDetallePaquete(null)}
          onEditar={(p) => { setDetallePaquete(null); setEditandoPaquete(p); }}
        />
      )}
      {detalleConsolidado && (
        <ConsolidadoDetalleModal
          consolidacion={detalleConsolidado}
          onClose={() => setDetalleConsolidado(null)}
          onDesconsolidar={detalleConsolidado.consolidacion_id ? () => {
            setDetalleConsolidado(null);
            desconsolidar(detalleConsolidado.consolidacion_id);
          } : undefined}
        />
      )}
      {liquidandoPaquetes && (
        <LiquidarModal
          paquetes={liquidandoPaquetes}
          rol={rol}
          onClose={() => setLiquidandoPaquetes(null)}
          onLiquidado={(liq) => {
            setLiquidandoPaquetes(null);
            setImagenLiquidacionId(liq.id);
            cargar();
          }}
        />
      )}
      {imagenLiquidacionId && (
        <LiquidacionImagenModal
          liquidacionId={imagenLiquidacionId}
          onClose={() => setImagenLiquidacionId(null)}
        />
      )}
      </>
      )}
      </div>
    </div>
  );
}
